import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { EventViewerStore, eventViewerStore } from '../store/eventviewer-store';
import { Loader } from '../component/loader';
import { PageContent } from '../component/pageContent';
import { DataTable } from '../component/table/dataTable';
import { Input, InputWithConfirm } from '../component/input';
import { Button, A11yButton } from '../component/button';
import { Select } from '../component/select';
import ReactTooltip from 'react-tooltip';
import styled, { keyframes } from 'styled-components';
import { Glyphicon } from '../component/icons';
import { eventViewerApi } from '../api/event-viewer';
import { copyTextToClipboard } from '../util/clipboard';
import { messageActions } from '../action';
import { theme } from '../styles/theme';
import { DateTimeInput } from '../component/dateTimeInput';

const LargePageBreak = 1000;
const SmallPageBreak = 800;

type EventViewerProps = {
  eventViewerStore: EventViewerStore;
};

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr;
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  margin-bottom: 20px;

  @media screen and (max-width: ${LargePageBreak}px) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${SmallPageBreak}px) {
    grid-template-columns: 1fr;
  }
`;

const SearchDiv = styled.div`
  display: flex;
  align-self: center;

  @media screen and (max-width: ${LargePageBreak}px) {
    grid-column: span 2 / -1;
  }

  @media screen and (max-width: ${SmallPageBreak}px) {
    grid-column: 1;
  }
`;

const SlideUpAnimation = keyframes`
  0% {
    bottom: -401px;
  }

  100% {
    bottom: 0;
  }
`;

const SlideUpDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 5px;
  grid-column-gap: 5px;
  right: 30px;
  bottom: 0;
  height: 450px;
  max-height: 90%;
  width: calc(100% - 290px);
  min-width: 400px;
  padding: 20px;
  position: fixed;
  z-index: 2;
  overflow-y: auto;
  border: 2px solid darkgray;
  box-shadow: 2px 2px 10px 0 rgba(0, 0, 0, 0.1),
    2px 2px 10px 0 rgba(101, 69, 167, 0.3);
  background-color: whitesmoke;
  animation-name: ${SlideUpAnimation};
  animation-direction: normal;
  animation-duration: 0.25s;
  animation-timing-function: ease;
  animation-fill-mode: forwards;
`;

const SlideOutAnimation = keyframes`
  0% {
    width: 0;
  }

  100% {
    width: 325px;
  }
`;

const FilterSidebarWrapper = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  width: 0;
  max-width: 95%;
  z-index: 2;
  padding: 20px 10px;
  border-left: 1px solid darkgray;
  box-shadow: -5px 0 15px gray;
  background-color: whitesmoke;
  animation-name: ${SlideOutAnimation};
  animation-direction: normal;
  animation-duration: 0.25s;
  animation-timing-function: ease;
  animation-fill-mode: forwards;
`;

@observer
class EventViewerComp extends Component<EventViewerProps> {
  constructor(props: EventViewerProps) {
    super(props);
  }

  componentDidMount() {
    eventViewerStore.initData();
  }

  componentWillUnmount() {}

  render() {
    return (
      <div>
        <ControlsGrid>
          <SearchDiv>
            <InputWithConfirm
              value={eventViewerStore.searchString.toString()}
              onChange={(ev, val) => {
                eventViewerStore.setSearchString(val);
              }}
              onConfirm={ev => {
                eventViewerStore.queryEventViewer();
              }}
              onEscape={ev => {
                eventViewerStore.setSearchString('');
                eventViewerStore.queryEventViewer();
              }}
              placeholder="Search by source, event ID, or description..."
              icon="search"
              iconStyle={{ color: theme.primaryColor }}
            />
          </SearchDiv>

          <Button
            icon="filter"
            content="Filter"
            type="button"
            style={{ justifySelf: 'center', alignSelf: 'center' }}
            onClick={() => {
              eventViewerStore.setFilterSidebarIsOpen(
                !eventViewerStore.isFilterSidebarOpen,
              );
            }}
          />
          <PaginationControls eventViewerStore={eventViewerStore} />
        </ControlsGrid>

        <FilterBanner isFilterApplied={eventViewerStore.isFilterApplied} />

        <EventTable eventViewerStore={eventViewerStore} />

        {/* Hide the bottom pagination controls until data is loaded. */}
        {eventViewerStore.eventRecords ? (
          <div style={{ display: 'grid', marginTop: '30px' }}>
            <PaginationControls eventViewerStore={eventViewerStore} />
          </div>
        ) : null}
      </div>
    );
  }
}

@observer
class EventTable extends React.Component<EventViewerProps> {
  constructor(props: EventViewerProps) {
    super(props);
  }

  render() {
    const {
      eventRecords,
      sortDirection,
      sortColumn,
      serviceId,
    } = eventViewerStore;

    if (!eventRecords) {
      return <Loader text="Loading events..." />;
    } else {
      const sortOrder = sortDirection == 'Ascending' ? 'asc' : 'desc';
      const sortField = sortColumn;
      return (
        <DataTable
          divided
          sortable
          rowHoverHighlight
          maxWidth
          initialSortField={
            (sortField.charAt(0).toLowerCase() +
              sortField.slice(1)) as keyof EventRecord
          }
          initialSortOrder={sortOrder}
          dataKey="recordId"
          data={eventRecords}
          rowSelectedCallback={item => {
            eventViewerApi.getRecordDetails(
              serviceId,
              item.logName,
              item.recordId,
            );
          }}
          columns={[
            {
              header: 'Level',
              render: t => {
                if (t.level == 'Audit Success') {
                  return (
                    <span>
                      <Glyphicon
                        glyph="key"
                        color="darkorange"
                        style={{ verticalAlign: 'middle' }}
                      />{' '}
                      {t.level}
                    </span>
                  );
                }
                if (t.level == 'Audit Failure') {
                  return (
                    <span>
                      <Glyphicon
                        glyph="lock"
                        color="darkorange"
                        style={{ verticalAlign: 'middle' }}
                      />{' '}
                      {t.level}
                    </span>
                  );
                }
                if (
                  t.level == 'Information' ||
                  t.level == 'Verbose' ||
                  t.level == 'LogAlways'
                ) {
                  return (
                    <span>
                      <Glyphicon
                        glyph="info"
                        color="#2599b6"
                        style={{ verticalAlign: 'middle' }}
                      />{' '}
                      {t.level}
                    </span>
                  );
                }
                if (t.level == 'Warning') {
                  return (
                    <span>
                      <Glyphicon
                        glyph="alertTriangle"
                        color="darkorange"
                        style={{ verticalAlign: 'middle' }}
                      />{' '}
                      {t.level}
                    </span>
                  );
                }
                if (t.level == 'Error' || t.level == 'Critical') {
                  return (
                    <span>
                      <Glyphicon
                        glyph="alertCircle"
                        color="red"
                        style={{ verticalAlign: 'middle' }}
                      />{' '}
                      {t.level}
                    </span>
                  );
                }
              },
              sortField: 'level',
              sortOverrideCallback: sortOrder => {
                var sortDirection =
                  sortOrder == 'asc'
                    ? 'Ascending'
                    : ('Descending' as EventViewerSortDirection);
                eventViewerStore.setSortOptions(sortDirection, 'Level');
              },
            },
            {
              header: 'Log',
              render: t => t.logName,
              sortField: 'logName',
              sortOverrideCallback: sortOrder => {
                var sortDirection =
                  sortOrder == 'asc'
                    ? 'Ascending'
                    : ('Descending' as EventViewerSortDirection);
                eventViewerStore.setSortOptions(sortDirection, 'LogName');
              },
            },
            {
              header: 'Date/Time',
              render: t => new Date(t.timeCreated).toLocaleString(),
              sortField: 'timeCreated',
              sortOverrideCallback: sortOrder => {
                var sortDirection =
                  sortOrder == 'asc'
                    ? 'Ascending'
                    : ('Descending' as EventViewerSortDirection);
                eventViewerStore.setSortOptions(sortDirection, 'TimeCreated');
              },
            },
            {
              header: 'Source',
              render: t => t.source,
              sortField: 'source',
              sortOverrideCallback: sortOrder => {
                var sortDirection =
                  sortOrder == 'asc'
                    ? 'Ascending'
                    : ('Descending' as EventViewerSortDirection);
                eventViewerStore.setSortOptions(sortDirection, 'Source');
              },
            },
            {
              header: 'Event ID',
              render: t => t.eventId,
              sortField: 'eventId',
              sortOverrideCallback: sortOrder => {
                var sortDirection =
                  sortOrder == 'asc'
                    ? 'Ascending'
                    : ('Descending' as EventViewerSortDirection);
                eventViewerStore.setSortOptions(sortDirection, 'EventId');
              },
            },
          ]}
        />
      );
    }
  }
}

@observer
class RecordDetailPane extends React.Component<EventViewerProps> {
  constructor(props: EventViewerProps) {
    super(props);
  }

  copyRecordToClipboard(record: EventRecord) {
    var recordText = `Description\n`;
    recordText += `------------\n`;
    recordText += `${record.description}\n\n`;
    recordText += `Details\n`;
    recordText += `------------\n`;
    recordText += `Log Name: ${record.logName}\n`;
    recordText += `Source: ${record.source}\n`;
    recordText += `Event ID: ${record.eventId}\n`;
    recordText += `Level: ${record.level}\n`;
    recordText += `User: ${record.userName}\n`;
    recordText += `Task Category: ${record.taskCategory}\n`;
    recordText += `Logged: ${new Date(record.timeCreated).toLocaleString()}\n`;

    copyTextToClipboard(recordText);

    messageActions.plainMessage('Copied to clipboard!');
  }

  render() {
    const descriptionTextCss: React.CSSProperties = {
      resize: 'none',
      height: '345px',
      width: '100%',
      padding: '5px',
    };

    const detailsDivCss: React.CSSProperties = {
      height: '345px',
      width: '100%',
      padding: '5px',
      backgroundColor: 'white',
      border: '1px solid rgb(169,169,169)',
      overflowY: 'auto',
    };

    const detailsGridCss: React.CSSProperties = {
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      gridColumnGap: '10px',
      gridRowGap: '5px',
    };

    if (eventViewerStore.selectedRecord) {
      var record = eventViewerStore.selectedRecord;
      return (
        <SlideUpDiv>
          <h3 style={{ alignSelf: 'start', marginTop: 0 }}>Event Details</h3>
          <div style={{ justifySelf: 'end', alignSelf: 'start' }}>
            <Button
              primary
              content="Copy"
              icon="copy"
              style={{ marginRight: '15px' }}
              onClick={() =>
                this.copyRecordToClipboard(eventViewerStore.selectedRecord)
              }
            />
            <Button
              primary
              icon="x"
              onClick={() => eventViewerStore.setSelectedRecord(null)}
            />
          </div>

          <div>
            <label>Description</label>
            <textarea
              readOnly
              value={record.description || ''}
              style={descriptionTextCss}
            />
          </div>
          <div>
            <label>Details</label>
            <div style={detailsDivCss}>
              <div style={detailsGridCss}>
                <span>Log Name:</span>
                <span>{record.logName}</span>
                <span>Source:</span>
                <span>{record.source}</span>
                <span>Event ID:</span>
                <span>{record.eventId}</span>
                <span>Level:</span>
                <span>
                  {record.level == 'LogAlways' ||
                  record.level == 'Audit Failure' ||
                  record.level == 'Audit Success'
                    ? 'Information'
                    : record.level}
                </span>
                <span>User:</span>
                <span>{record.userName}</span>
                <span>Task Category:</span>
                <span>{record.taskCategory}</span>
                <span>Logged:</span>
                <span>{new Date(record.timeCreated).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </SlideUpDiv>
      );
    } else {
      return null;
    }
  }
}

const PaginationWrapper = styled.div`
  justify-self: center;
`;

@observer
class PaginationControls extends React.Component<EventViewerProps> {
  constructor(props: EventViewerProps) {
    super(props);
  }

  render() {
    const { currentPage, totalPages } = eventViewerStore;

    return (
      <PaginationWrapper>
        <div style={{ textAlign: 'center' }}>
          <label>Page</label>
        </div>
        <div style={{ display: 'flex' }}>
          <Button
            icon="arrowLeft"
            data-tip
            data-for="back-tooltip"
            aria-label="Page back"
            style={{ marginRight: '5px' }}
            onClick={() => eventViewerStore.pageDown()}
          />

          <ReactTooltip
            id="back-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span>Back</span>
          </ReactTooltip>

          <Input
            style={{ width: '80px', textAlign: 'center' }}
            value={currentPage.toString()}
            onChange={ev => eventViewerStore.setPage(ev)}
          />

          <Button
            icon="arrowRight"
            data-tip
            data-for="forward-tooltip"
            aria-label="Page forward"
            style={{ marginLeft: '5px' }}
            onClick={() => eventViewerStore.pageUp()}
          />

          <ReactTooltip
            id="forward-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span>Forward</span>
          </ReactTooltip>
        </div>
        <div style={{ textAlign: 'center', marginTop: '5px', color: 'gray' }}>
          of {totalPages}
        </div>
      </PaginationWrapper>
    );
  }
}

@observer
class FilterSidebar extends React.Component<EventViewerProps> {
  constructor(props) {
    super(props);
  }
  render() {
    if (!eventViewerStore.isFilterSidebarOpen) {
      return null;
    }
    const logNames = eventViewerStore.logNames.map(x => ({
      value: x,
      label: x,
    }));
    const selectedNames = eventViewerStore.selectedLogNames.map(x => ({
      value: x,
      label: x,
    }));
    const logLevels = eventViewerStore.logLevels.map(x => ({
      value: x,
      label: x,
    }));
    const selectedLevels = eventViewerStore.selectedLevels.map(x => ({
      value: x,
      label: x,
    }));

    return (
      <FilterSidebarWrapper>
        <div>
          <div>
            <div style={{ display: 'flex' }}>
              <h3 style={{ flex: 'auto', marginTop: '5px' }}>Filter Records</h3>
              <Button
                primary
                icon="x"
                style={{ alignSelf: 'center' }}
                onClick={() => eventViewerStore.setFilterSidebarIsOpen(false)}
              />
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <label>Logs</label>
          </div>
          <div>
            <Select
              isMulti
              closeMenuOnSelect={false}
              placeholder="All Logs"
              options={logNames}
              value={selectedNames}
              onChange={ev => {
                eventViewerStore.updateSelectedLogNames(ev as any);
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: '10px' }}>
          <div>
            <label>Levels</label>
          </div>
          <Select
            isMulti
            closeMenuOnSelect={false}
            placeholder="All Levels"
            options={logLevels}
            value={selectedLevels}
            onChange={ev => {
              eventViewerStore.updateSelectedLogLevels(ev as any);
            }}
          />
        </div>

        <div style={{ marginTop: '10px' }}>
          <label>From</label>
        </div>

        <div style={{ display: 'flex' }}>
          <DateTimeInput
            type="date"
            value={eventViewerStore.fromDate.toString()}
            onChange={ev => {
              eventViewerStore.setFromDate(ev.target.value);
            }}
            style={{ flex: 'auto', marginRight: '5px' }}
          />
          <DateTimeInput
            type="time"
            value={eventViewerStore.fromTime.toString()}
            onChange={ev => {
              eventViewerStore.setFromTime(ev.target.value);
            }}
            style={{ flex: 'auto' }}
          />
        </div>

        <div style={{ marginTop: '10px' }}>
          <label>To</label>
        </div>
        <div style={{ display: 'flex' }}>
          <DateTimeInput
            type="date"
            value={eventViewerStore.toDate.toString()}
            onChange={ev => {
              eventViewerStore.setToDate(ev.target.value);
            }}
            style={{ flex: 'auto', marginRight: '5px' }}
          />
          <DateTimeInput
            type="time"
            value={eventViewerStore.toTime.toString()}
            onChange={ev => {
              eventViewerStore.setToTime(ev.target.value);
            }}
            style={{ flex: 'auto' }}
          />
        </div>

        {!eventViewerStore.isDateTimeFilterValid ? (
          <div style={{ textAlign: 'center', color: 'red', marginTop: '5px' }}>
            Invalid Range
          </div>
        ) : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            marginTop: '10px',
          }}
        >
          <Button
            style={{ justifySelf: 'start' }}
            content="Clear"
            onClick={() => {
              eventViewerStore.setFilterSidebarIsOpen(false);
              eventViewerStore.clearFilter();
            }}
          />
          <Button
            style={{ justifySelf: 'end' }}
            disabled={!eventViewerStore.isDateTimeFilterValid}
            primary
            content="Apply"
            onClick={() => {
              eventViewerStore.setFilterSidebarIsOpen(false);
              eventViewerStore.queryEventViewer();
            }}
          />
        </div>
      </FilterSidebarWrapper>
    );
  }
}

const FilterBanner: React.FunctionComponent<{
  isFilterApplied: boolean;
}> = props => {
  if (!props.isFilterApplied) {
    return null;
  }

  return (
    <div style={{ display: 'flex' }}>
      <Glyphicon
        style={{ alignSelf: 'center' }}
        glyph="filter"
        size="big"
        color={theme.primaryColor}
      />

      <span
        style={{ alignSelf: 'center', fontSize: '1.2em', marginLeft: '5px' }}
      >
        Filter applied.
      </span>

      <A11yButton
        linkLook
        style={{
          alignSelf: 'flex-end',
          width: 'auto',
          marginLeft: '5px',
          cursor: 'pointer',
        }}
        onClick={() => {
          eventViewerStore.clearFilter();
        }}
      >
        Clear
      </A11yButton>
    </div>
  );
};

export function EventViewerPage(props: { path: string }) {
  return (
    <PageContent header="Event Viewer">
      <EventViewerComp eventViewerStore={eventViewerStore} />

      <FilterSidebar eventViewerStore={eventViewerStore} />

      <RecordDetailPane eventViewerStore={eventViewerStore} />
    </PageContent>
  );
}
